import { useNavigate, useSearch } from '@tanstack/react-router'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import {
  DocumentHistoryItem,
  DocumentTab,
  type FragmentHighlight,
  type RegionHighlight,
  SearchHistoryItem,
  type SearchStateUpdate,
  TabHistoryItem,
  TimeSeriesHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import {
  getPersistedTabState,
  persistTabState,
} from 'features/knowledgeDocumentViewer/utils/documentPageSessionStorage'
import { reconcileTabsWithDocumentsUrl } from 'features/knowledgeDocumentViewer/utils/reconcileTabsWithDocumentsUrl'
import { serializeSelectedRegionsParam } from 'features/knowledgeDocumentViewer/utils/selectedRegionsParam'
import {
  applySearchUrlPatchToTabs,
  CLEAR_ALL_VIEWER_PARAMS,
  CLEAR_DOCUMENT_PARAMS,
  createDefaultTab,
  createDocumentTab,
  createInitialTab,
  mergeSearchStateIntoTabs,
  searchParamsFromSearchState,
} from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import {
  chartLocalStateKey,
  clearChartLocalState,
} from 'features/timeSeries/utils/chartLocalState'
import { useOptionalWorkspaceTabs } from 'features/workspaceTabs/WorkspaceTabsContext'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** Order-sensitive string-array equality. */
const sameStringArray = (a?: string[], b?: string[]): boolean =>
  (a?.length ?? 0) === (b?.length ?? 0) &&
  (a ?? []).every((x, i) => x === (b ?? [])[i])

/** Shallow equality of a `{ id: color }` map (key-by-key, no JSON round-trip). */
const sameColorMap = (
  a?: Record<string, string>,
  b?: Record<string, string>
): boolean => {
  const ak = Object.keys(a ?? {})
  const bk = Object.keys(b ?? {})
  return ak.length === bk.length && ak.every((k) => a?.[k] === b?.[k])
}
import {
  CLEAR_SEARCH_VIEWER_PARAMS,
  DocumentsSearchParams,
  DocumentViewerUrlPaths,
  getSearchStatePatchFromUrl,
} from 'routes/documents/-searchParams'

export const useTabState = (
  basePath: DocumentViewerUrlPaths,
  siteId: string
) => {
  const search = useSearch({ from: basePath })
  const { isPreviewMode } = usePipelineConfig()
  const tabStateScope = `${siteId}:${isPreviewMode ? 'preview' : 'active'}`
  // Dual path: when WorkspaceTabsProvider is mounted (embedded sidebar), tab
  // open/active state is owned there. When absent, this hook owns local state +
  // session persistence. Full Cantilever rollout deletes the local branch and
  // DocumentTabs chrome together — do not add new embeddedSidebar checks here.
  const workspaceTabs = useOptionalWorkspaceTabs()

  const [initialState] = useState(() => {
    const persisted = getPersistedTabState(tabStateScope)
    const hasUrlDocument = !!search.documentID

    let result: { openTabs: DocumentTab[]; activeTabId: string }

    if (hasUrlDocument && persisted) {
      const activePersisted = persisted.openTabs.find(
        (t) => t.id === persisted.activeTabId
      )
      const activeItem = activePersisted?.history[activePersisted.currentIndex]
      if (
        activeItem?.type === 'document' &&
        activeItem.documentId === search.documentID
      ) {
        result = {
          openTabs: persisted.openTabs,
          activeTabId: persisted.activeTabId,
        }
      } else {
        const existingTab = persisted.openTabs.find((t) => {
          const item = t.history[t.currentIndex]
          return (
            item.type === 'document' && item.documentId === search.documentID
          )
        })
        if (existingTab) {
          result = { openTabs: persisted.openTabs, activeTabId: existingTab.id }
        } else {
          const newTab = createInitialTab(
            search.documentID,
            search.pageNumber,
            search.selectedBoxes,
            search.selectedRegions
          )
          result = {
            openTabs: [...persisted.openTabs, newTab],
            activeTabId: newTab.id,
          }
        }
      }
    } else if (hasUrlDocument) {
      const tab = createInitialTab(
        search.documentID,
        search.pageNumber,
        search.selectedBoxes,
        search.selectedRegions
      )
      result = { openTabs: [tab], activeTabId: tab.id }
    } else if (persisted) {
      result = {
        openTabs: persisted.openTabs,
        activeTabId: persisted.activeTabId,
      }
    } else {
      const tab = createDefaultTab()
      result = { openTabs: [tab], activeTabId: tab.id }
    }

    if (!hasUrlDocument) {
      const urlPatch = getSearchStatePatchFromUrl(search)
      if (urlPatch && Object.keys(urlPatch).length > 0) {
        result = applySearchUrlPatchToTabs(
          result.openTabs,
          result.activeTabId,
          urlPatch
        )
      }
    }

    return result
  })

  const [localOpenTabs, setLocalOpenTabs] = useState(initialState.openTabs)
  const [localActiveTabId, setLocalActiveTabId] = useState<string>(
    initialState.activeTabId
  )
  const openTabs = workspaceTabs?.openTabs ?? localOpenTabs
  const setOpenTabs = workspaceTabs?.setOpenTabs ?? setLocalOpenTabs
  const activeTabId = workspaceTabs?.activeTabId ?? localActiveTabId
  const setActiveTabId = workspaceTabs?.setActiveTabId ?? setLocalActiveTabId

  /** Ref to the latest openTabs value for synchronous reads outside of state updaters */
  const openTabsRef = useRef(openTabs)
  openTabsRef.current = openTabs

  const activeTabIdRef = useRef(activeTabId)
  activeTabIdRef.current = activeTabId

  // Legacy-only persistence (skipped when workspace provider owns tabs).
  useEffect(() => {
    if (workspaceTabs) return
    const timer = setTimeout(() => {
      persistTabState(tabStateScope, openTabs, activeTabId)
    }, 300)
    return () => clearTimeout(timer)
  }, [openTabs, activeTabId, tabStateScope, workspaceTabs])

  // Legacy-only unmount flush.
  useEffect(() => {
    if (workspaceTabs) return
    return () => {
      persistTabState(
        tabStateScope,
        openTabsRef.current,
        activeTabIdRef.current
      )
    }
  }, [tabStateScope, workspaceTabs])

  const activeTab = useMemo(() => {
    return openTabs.find((t) => t.id === activeTabId) ?? openTabs[0]
  }, [openTabs, activeTabId])

  const navigate = useNavigate({ from: basePath })

  const updateURLParams = useCallback(
    (updates: DocumentsSearchParams) => {
      navigate({
        search: (prev) => ({
          ...prev,
          ...updates,
        }),
        replace: true,
      })
    },
    [navigate]
  )

  const writeSearchStateToURL = useCallback(
    (ss: SearchHistoryItem['searchState']) => {
      updateURLParams({
        ...CLEAR_DOCUMENT_PARAMS,
        ...searchParamsFromSearchState(ss),
      })
    },
    [updateURLParams]
  )

  const writeHistoryItemToURL = useCallback(
    (item: TabHistoryItem) => {
      if (item.type === 'document') {
        updateURLParams({
          ...CLEAR_SEARCH_VIEWER_PARAMS,
          documentID: item.documentId,
          pageNumber: item.pageNumber,
          selectedBoxes: item.selectedBoxIds,
          selectedRegions: serializeSelectedRegionsParam(
            item.regionHighlight?.boxes ?? []
          ),
        })
      } else if (item.type === 'search') {
        writeSearchStateToURL(item.searchState)
      } else {
        updateURLParams(CLEAR_ALL_VIEWER_PARAMS)
      }
    },
    [updateURLParams, writeSearchStateToURL]
  )

  const setWorkspaceScope = workspaceTabs?.setWorkspaceScope
  const previousTabStateScopeRef = useRef(tabStateScope)
  useEffect(() => {
    if (setWorkspaceScope) {
      setWorkspaceScope(tabStateScope)
      return
    }
    if (previousTabStateScopeRef.current === tabStateScope) return
    previousTabStateScopeRef.current = tabStateScope

    const persisted = getPersistedTabState(tabStateScope)
    if (persisted) {
      setOpenTabs(persisted.openTabs)
      setActiveTabId(persisted.activeTabId)
      const activeTab =
        persisted.openTabs.find((tab) => tab.id === persisted.activeTabId) ??
        persisted.openTabs[0]
      if (activeTab) {
        writeHistoryItemToURL(activeTab.history[activeTab.currentIndex])
      }
      return
    }

    const freshTab = createDefaultTab()
    setOpenTabs([freshTab])
    setActiveTabId(freshTab.id)
    writeHistoryItemToURL(freshTab.history[0])
  }, [
    setActiveTabId,
    setOpenTabs,
    setWorkspaceScope,
    tabStateScope,
    writeHistoryItemToURL,
  ])

  // On mount: prefer URL document/search intent over persisted tab→URL writes.
  // With workspace tabs, this also reconciles provider state that ignored URL init.
  const hasSyncedUrlRef = useRef(false)
  useEffect(() => {
    if (hasSyncedUrlRef.current) return
    hasSyncedUrlRef.current = true

    const reconciled = reconcileTabsWithDocumentsUrl(
      openTabsRef.current,
      activeTabIdRef.current,
      search
    )
    if (reconciled) {
      setOpenTabs(reconciled.openTabs)
      setActiveTabId(reconciled.activeTabId)
      return
    }

    const tab = openTabsRef.current.find((t) => t.id === activeTabIdRef.current)
    if (tab) {
      writeHistoryItemToURL(tab.history[tab.currentIndex])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const addTab = useCallback(
    (tab: DocumentTab) => {
      if (openTabsRef.current.some((t) => t.id === tab.id)) {
        setActiveTabId(tab.id)
        return
      }
      setOpenTabs((prevTabs) => {
        if (prevTabs.some((t) => t.id === tab.id)) {
          return prevTabs
        }
        return [...prevTabs, tab]
      })
      writeHistoryItemToURL(tab.history[tab.currentIndex])
      setActiveTabId(tab.id)
    },
    [setActiveTabId, setOpenTabs, writeHistoryItemToURL]
  )

  const addDocumentTab = useCallback(
    ({
      documentId,
      pageNumber = 1,
      selectedBoxIds = [],
      query = '',
      searchOriginQuery,
      fragmentHighlight,
      regionHighlight,
    }: {
      documentId: string
      pageNumber?: number
      selectedBoxIds?: string[]
      query?: string
      searchOriginQuery?: string
      fragmentHighlight?: FragmentHighlight
      regionHighlight?: RegionHighlight
    }) => {
      addTab(
        createDocumentTab(
          documentId,
          pageNumber,
          selectedBoxIds,
          query,
          fragmentHighlight,
          searchOriginQuery,
          regionHighlight
        )
      )
    },
    [addTab]
  )

  const removeTab = useCallback(
    (tabId: string) => {
      if (workspaceTabs) {
        workspaceTabs.closeWorkspaceTab(tabId)
        return
      }

      // A time-series tab persists its unsaved local state to localStorage
      // keyed by this tab id (see EmbeddedTimeSeriesChart / ScatterplotProvider
      // localStateScope). Closing the tab discards that state, so clear the key
      // rather than leak an orphaned entry no future mount will ever read.
      // Harmless no-op for non-time-series tabs, which never wrote such a key.
      clearChartLocalState(chartLocalStateKey(siteId, tabId))

      if (openTabs.length === 1) {
        const freshTab = createDefaultTab()
        setOpenTabs([freshTab])
        setActiveTabId(freshTab.id)
        writeHistoryItemToURL(freshTab.history[0])
        return
      }

      const newTabs = openTabs.filter((t) => t.id !== tabId)
      setOpenTabs(newTabs)

      if (activeTabId === tabId && newTabs.length > 0) {
        const newActiveTab = newTabs[newTabs.length - 1]
        setActiveTabId(newActiveTab.id)
        writeHistoryItemToURL(newActiveTab.history[newActiveTab.currentIndex])
      }
    },
    [
      openTabs,
      activeTabId,
      siteId,
      setActiveTabId,
      setOpenTabs,
      workspaceTabs,
      writeHistoryItemToURL,
    ]
  )

  const pushToHistory = useCallback(
    (tabId: string, item: TabHistoryItem) => {
      setOpenTabs((prevTabs) => {
        return prevTabs.map((tab) => {
          if (tab.id !== tabId) return tab
          const newHistory = [
            ...tab.history.slice(0, tab.currentIndex + 1),
            item,
          ]
          return {
            ...tab,
            history: newHistory,
            currentIndex: newHistory.length - 1,
          }
        })
      })
      if (tabId === activeTabId) {
        writeHistoryItemToURL(item)
      }
    },
    [setOpenTabs, activeTabId, writeHistoryItemToURL]
  )

  const navigateHistory = useCallback(
    (tabId: string, direction: 'back' | 'forward') => {
      const tab = openTabs.find((t) => t.id === tabId)
      if (!tab) return

      const newIndex =
        direction === 'back' ? tab.currentIndex - 1 : tab.currentIndex + 1

      if (newIndex < 0 || newIndex >= tab.history.length) return

      const targetItem = tab.history[newIndex]

      setOpenTabs((prevTabs) =>
        prevTabs.map((t) =>
          t.id === tabId ? { ...t, currentIndex: newIndex } : t
        )
      )

      if (tabId === activeTabId) {
        writeHistoryItemToURL(targetItem)
      }
    },
    [openTabs, activeTabId, setOpenTabs, writeHistoryItemToURL]
  )

  const goBack = useCallback(
    (tabId: string) => navigateHistory(tabId, 'back'),
    [navigateHistory]
  )

  const goForward = useCallback(
    (tabId: string) => navigateHistory(tabId, 'forward'),
    [navigateHistory]
  )

  const updateDocumentHistoryItem = useCallback(
    (tabId: string, updates: Partial<Omit<DocumentHistoryItem, 'type'>>) => {
      setOpenTabs((prevTabs) => {
        return prevTabs.map((tab) => {
          if (tab.id !== tabId) return tab
          const currentItem = tab.history[tab.currentIndex]
          if (currentItem.type !== 'document') return tab
          const updatedItem: DocumentHistoryItem = {
            ...currentItem,
            ...updates,
          }
          const newHistory = [...tab.history]
          newHistory[tab.currentIndex] = updatedItem
          return { ...tab, history: newHistory }
        })
      })

      if (tabId === activeTabId) {
        updateURLParams({
          ...CLEAR_SEARCH_VIEWER_PARAMS,
          ...(updates.documentId !== undefined && {
            documentID: updates.documentId,
          }),
          ...(updates.pageNumber !== undefined && {
            pageNumber: updates.pageNumber,
          }),
          ...(updates.selectedBoxIds !== undefined && {
            selectedBoxes: updates.selectedBoxIds,
          }),
          // `in` (not !== undefined): clearing the highlight passes
          // regionHighlight: undefined explicitly, which must clear the param.
          ...('regionHighlight' in updates && {
            selectedRegions: serializeSelectedRegionsParam(
              updates.regionHighlight?.boxes ?? []
            ),
          }),
        })
      }
    },
    [setOpenTabs, activeTabId, updateURLParams]
  )

  const updateSearchHistoryItem = useCallback(
    (tabId: string, searchStateUpdates: SearchStateUpdate) => {
      setOpenTabs((prevTabs) => {
        const { nextTabs, urlSync } = mergeSearchStateIntoTabs(
          prevTabs,
          tabId,
          activeTabId,
          searchStateUpdates
        )
        if (urlSync) {
          writeSearchStateToURL(urlSync)
        }
        return nextTabs
      })
    },
    [activeTabId, setOpenTabs, writeSearchStateToURL]
  )

  /**
   * Update the current time-series history item of `tabId` in place (no new
   * history entry, no URL sync — time-series items carry no URL params).
   * Used to upgrade a transient tab with the chart id minted by its first
   * save, and to sync the tab label to the saved chart name.
   */
  const updateTimeSeriesHistoryItem = useCallback(
    (tabId: string, updates: Partial<Omit<TimeSeriesHistoryItem, 'type'>>) => {
      setOpenTabs((prevTabs) =>
        prevTabs.map((tab) => {
          if (tab.id !== tabId) return tab
          const currentItem = tab.history[tab.currentIndex]
          if (currentItem.type !== 'timeseries') return tab
          const updatedItem: TimeSeriesHistoryItem = {
            ...currentItem,
            ...updates,
          }
          const newHistory = [...tab.history]
          newHistory[tab.currentIndex] = updatedItem
          return { ...tab, history: newHistory }
        })
      )
    },
    [setOpenTabs]
  )

  const addSensorsToTimeSeriesTab = useCallback(
    (tabId: string, sensorIdsToAdd: string[]) => {
      setOpenTabs((prevTabs) => {
        return prevTabs.map((tab) => {
          if (tab.id !== tabId) return tab
          const currentItem = tab.history[tab.currentIndex]
          if (currentItem.type !== 'timeseries') return tab
          // Saved-chart tabs render from the resolved chart config and ignore
          // `sensorIds` seeding — adding ids here would silently diverge.
          if (currentItem.chartID) return tab
          const existingSensorIds = new Set(currentItem.sensorIds)
          const newSensorIds = sensorIdsToAdd.filter(
            (id) => !existingSensorIds.has(id)
          )
          if (newSensorIds.length === 0) return tab
          const updatedItem: TimeSeriesHistoryItem = {
            ...currentItem,
            sensorIds: [...currentItem.sensorIds, ...newSensorIds],
          }
          const newHistory = [...tab.history]
          newHistory[tab.currentIndex] = updatedItem
          return { ...tab, history: newHistory }
        })
      })
    },
    [setOpenTabs]
  )

  // Replace a time-series tab's sensor set (and optional colors/deemphasis) in
  // place — no new history entry — so a controlled chart reflects add AND remove
  // without remounting. Used by the claims pinned process-data tab.
  const setTimeSeriesTabSensors = useCallback(
    (
      tabId: string,
      sensorIds: string[],
      options?: {
        sensorColors?: Record<string, string>
        deemphasizedSensorIds?: string[]
      }
    ) => {
      setOpenTabs((prevTabs) => {
        let changed = false
        const nextTabs = prevTabs.map((tab) => {
          if (tab.id !== tabId) return tab
          const currentItem = tab.history[tab.currentIndex]
          if (currentItem.type !== 'timeseries') return tab
          // Saved-chart tabs render from the resolved chart config and ignore
          // `sensorIds` seeding — replacing ids here would silently diverge
          // the live tab from what a restore renders (same guard as
          // addSensorsToTimeSeriesTab).
          if (currentItem.chartID) return tab
          const nextColors = options?.sensorColors ?? currentItem.sensorColors
          const nextDeemph =
            options?.deemphasizedSensorIds ?? currentItem.deemphasizedSensorIds
          if (
            sameStringArray(currentItem.sensorIds, sensorIds) &&
            sameColorMap(currentItem.sensorColors, nextColors) &&
            sameStringArray(currentItem.deemphasizedSensorIds, nextDeemph)
          ) {
            return tab
          }
          changed = true
          const updatedItem: TimeSeriesHistoryItem = {
            ...currentItem,
            sensorIds,
            sensorColors: nextColors,
            deemphasizedSensorIds: nextDeemph,
          }
          const newHistory = [...tab.history]
          newHistory[tab.currentIndex] = updatedItem
          return { ...tab, history: newHistory }
        })
        // Return the SAME array reference when nothing changed so React bails out
        // of the state update — otherwise a per-render caller would loop forever.
        return changed ? nextTabs : prevTabs
      })
    },
    [setOpenTabs]
  )

  // Prepend a pinned (closable === false) time-series tab iff none exists, using
  // a FUNCTIONAL update so it composes with a sibling effect's reorderTabs in the
  // same commit instead of clobbering it from a stale snapshot.
  const prependPinnedTimeSeriesTab = useCallback(
    (tab: DocumentTab) => {
      setOpenTabs((prevTabs) =>
        prevTabs.some(
          (t) =>
            t.closable === false &&
            t.history[t.currentIndex]?.type === 'timeseries'
        )
          ? prevTabs
          : [tab, ...prevTabs]
      )
    },
    [setOpenTabs]
  )

  // Move a tab to the front (functional; no-op when already first).
  const moveTabToFront = useCallback(
    (tabId: string) => {
      setOpenTabs((prevTabs) => {
        const idx = prevTabs.findIndex((t) => t.id === tabId)
        if (idx <= 0) return prevTabs
        const tab = prevTabs[idx]
        return [tab, ...prevTabs.slice(0, idx), ...prevTabs.slice(idx + 1)]
      })
    },
    [setOpenTabs]
  )

  const renameTab = useCallback(
    (tabId: string, label: string) => {
      setOpenTabs((prevTabs) =>
        prevTabs.map((tab) =>
          tab.id === tabId ? { ...tab, customLabel: label || undefined } : tab
        )
      )
    },
    [setOpenTabs]
  )

  const reorderTabs = useCallback(
    (newTabs: DocumentTab[]) => {
      setOpenTabs(newTabs)
    },
    [setOpenTabs]
  )

  const canGoBack = useMemo(() => {
    return activeTab.currentIndex > 0
  }, [activeTab])

  const canGoForward = useMemo(() => {
    return activeTab.currentIndex < activeTab.history.length - 1
  }, [activeTab])

  const activeHistoryItem = activeTab.history[activeTab.currentIndex]

  const switchToTab = useCallback(
    (tabId: string) => {
      const tab = openTabs.find((t) => t.id === tabId)
      if (tab) {
        setActiveTabId(tabId)
        writeHistoryItemToURL(tab.history[tab.currentIndex])
      }
    },
    [openTabs, setActiveTabId, writeHistoryItemToURL]
  )

  return {
    openTabs,
    activeTabId,
    setActiveTabId: switchToTab,
    addTab,
    addDocumentTab,
    removeTab,
    renameTab,
    reorderTabs,
    activeHistoryItem,
    pushToHistory,
    goBack,
    goForward,
    updateDocumentHistoryItem,
    updateSearchHistoryItem,
    updateTimeSeriesHistoryItem,
    addSensorsToTimeSeriesTab,
    setTimeSeriesTabSensors,
    prependPinnedTimeSeriesTab,
    moveTabToFront,
    canGoBack,
    canGoForward,
  }
}
